import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, Clock, User, Building2, Mail, Phone, MessageSquare, 
  RefreshCw, CheckCircle, ChevronLeft, ChevronRight, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [staffData, setStaffData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Selection state
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Form state
  const [formData, setFormData] = useState({
    booker_name: '',
    booker_company: '',
    booker_email: '',
    booker_phone: '',
    reason_for_meeting: '',
    is_existing_client: false
  });

  useEffect(() => {
    fetchStaffData();
  }, [slug]);

  const fetchStaffData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/booking/${slug}`);
      setStaffData(res.data);
      if (res.data.appointment_durations?.length > 0) {
        setSelectedDuration(res.data.appointment_durations[0]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Booking page not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime || !selectedDuration) {
      alert('Please select a date, time, and duration');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/booking/${slug}`, {
        ...formData,
        date: selectedDate,
        start_time: selectedTime,
        duration: selectedDuration
      });
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to book appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    for (let i = 0; i < startingDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const isDateAvailable = (date) => {
    if (!date || !staffData) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    
    // Check advance days limit
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + (staffData.advance_days || 30));
    if (date > maxDate) return false;
    
    const dayName = DAYS[date.getDay()].toLowerCase();
    const dayConfig = staffData.available_days?.[dayName];
    return dayConfig?.enabled && dayConfig?.slots?.length > 0;
  };

  const getAvailableSlots = (date) => {
    if (!date || !staffData || !selectedDuration) return [];
    
    const dayName = DAYS[date.getDay()].toLowerCase();
    const dayConfig = staffData.available_days?.[dayName];
    if (!dayConfig?.enabled) return [];
    
    const slots = [];
    const dateStr = date.toISOString().split('T')[0];
    const bookedSlots = staffData.booked_slots?.filter(b => b.date === dateStr) || [];
    
    dayConfig.slots?.forEach(slot => {
      let currentMinutes = timeToMinutes(slot.start);
      const endMinutes = timeToMinutes(slot.end);
      
      while (currentMinutes + selectedDuration <= endMinutes) {
        const timeStr = minutesToTime(currentMinutes);
        const endTimeStr = minutesToTime(currentMinutes + selectedDuration + (staffData.buffer_time || 15));
        
        // Check if this slot is already booked
        const isBooked = bookedSlots.some(b => {
          const bStart = timeToMinutes(b.start_time);
          const bEnd = timeToMinutes(b.end_time) + (b.buffer_time || 15);
          return (currentMinutes < bEnd && currentMinutes + selectedDuration + (staffData.buffer_time || 15) > bStart);
        });
        
        if (!isBooked) {
          slots.push(timeStr);
        }
        
        currentMinutes += 15; // 15 min intervals
      }
    });
    
    return slots;
  };

  const timeToMinutes = (time) => {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };

  const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  const formatTime = (time) => {
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#014DB7]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Page Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Meeting Booked!</h1>
          <p className="text-gray-600 mb-6">
            Your meeting with {staffData.staff_name} has been confirmed.
          </p>
          <div className="bg-blue-50 rounded-xl p-6 text-left">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Date</p>
                <p className="font-semibold">{new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
              </div>
              <div>
                <p className="text-gray-500">Time</p>
                <p className="font-semibold">{formatTime(selectedTime)}</p>
              </div>
              <div>
                <p className="text-gray-500">Duration</p>
                <p className="font-semibold">{selectedDuration} minutes</p>
              </div>
              <div>
                <p className="text-gray-500">With</p>
                <p className="font-semibold">{staffData.staff_name}</p>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            A confirmation email has been sent to {formData.booker_email}
          </p>
        </div>
      </div>
    );
  }

  const availableSlots = selectedDate ? getAvailableSlots(new Date(selectedDate)) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#014DB7] text-white py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-2">Book a Meeting</h1>
          <p className="text-blue-200">with {staffData.staff_name}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Calendar & Time Selection */}
          <div className="space-y-6">
            {/* Duration Selection */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Select Duration</h3>
              <div className="flex flex-wrap gap-2">
                {staffData.appointment_durations?.map(d => (
                  <button
                    key={d}
                    onClick={() => { setSelectedDuration(d); setSelectedTime(null); }}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      selectedDuration === d 
                        ? 'bg-[#014DB7] text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {d} min
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-semibold text-gray-900">
                  {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h3>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-center text-sm font-medium text-gray-500 py-2">{d}</div>
                ))}
                {getDaysInMonth(currentMonth).map((day, idx) => {
                  if (!day) return <div key={`empty-${idx}`} className="h-10" />;
                  const dateStr = day.toISOString().split('T')[0];
                  const isAvailable = isDateAvailable(day);
                  const isSelected = selectedDate === dateStr;
                  const isToday = day.toDateString() === new Date().toDateString();
                  
                  return (
                    <button
                      key={dateStr}
                      onClick={() => isAvailable && setSelectedDate(dateStr)}
                      disabled={!isAvailable}
                      className={`h-10 rounded-lg font-medium transition-colors ${
                        isSelected ? 'bg-[#014DB7] text-white' :
                        isAvailable ? 'hover:bg-blue-100 text-gray-900' :
                        'text-gray-300 cursor-not-allowed'
                      } ${isToday && !isSelected ? 'ring-2 ring-[#014DB7]' : ''}`}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Available Times for {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </h3>
                {availableSlots.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No available times for this date</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                    {availableSlots.map(time => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedTime === time
                            ? 'bg-[#014DB7] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-blue-100'
                        }`}
                      >
                        {formatTime(time)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Booking Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="font-semibold text-gray-900 mb-6">Your Information</h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={formData.booker_name}
                    onChange={(e) => setFormData({ ...formData, booker_name: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#014DB7] focus:border-[#014DB7]"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={formData.booker_company}
                    onChange={(e) => setFormData({ ...formData, booker_company: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#014DB7] focus:border-[#014DB7]"
                    placeholder="Acme Inc."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={formData.booker_email}
                    onChange={(e) => setFormData({ ...formData, booker_email: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#014DB7] focus:border-[#014DB7]"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="tel"
                    value={formData.booker_phone}
                    onChange={(e) => setFormData({ ...formData, booker_phone: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#014DB7] focus:border-[#014DB7]"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Meeting *</label>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <textarea
                    required
                    value={formData.reason_for_meeting}
                    onChange={(e) => setFormData({ ...formData, reason_for_meeting: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#014DB7] focus:border-[#014DB7] min-h-[100px]"
                    placeholder="What would you like to discuss?"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4">
                <input
                  type="checkbox"
                  id="existing_client"
                  checked={formData.is_existing_client}
                  onChange={(e) => setFormData({ ...formData, is_existing_client: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-[#014DB7] focus:ring-[#014DB7]"
                />
                <label htmlFor="existing_client" className="text-sm text-gray-700">
                  I am an existing client
                </label>
              </div>

              {/* Summary */}
              {selectedDate && selectedTime && selectedDuration && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <p className="text-sm font-medium text-blue-900 mb-2">Booking Summary</p>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p><Calendar className="w-4 h-4 inline mr-2" />{new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    <p><Clock className="w-4 h-4 inline mr-2" />{formatTime(selectedTime)} ({selectedDuration} min)</p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !selectedDate || !selectedTime || !selectedDuration}
                className="w-full py-4 bg-[#014DB7] text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Booking...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirm Booking
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Questions? Contact us at (941) 466-4-DME</p>
        </div>
      </div>
    </div>
  );
}
